import { useState, useEffect } from 'react';

/**
 * Champ de saisie de date sans sélecteur calendrier.
 *
 * L'utilisateur tape directement ex: `19500405` → formaté en `1950-04-05`.
 * Au `blur`, valide :
 *   1. Format `AAAAMMJJ` (8 chiffres)
 *   2. Date réelle (pas de 31 février)
 *   3. (Optionnel) Date ≥ `minDate` — pour empêcher p. ex. une date de signature
 *      antérieure à la date de demande.
 *
 * Props:
 *   value     : string YYYY-MM-DD (valeur contrôlée)
 *   onChange  : fn(string) appelée avec la valeur YYYY-MM-DD validée
 *   testId    : string (data-testid)
 *   style     : object (style additionnel sur l'input)
 *   disabled  : boolean
 *   minDate   : string YYYY-MM-DD (optionnel) — borne inférieure inclusive
 *   minLabel  : string (optionnel) — étiquette utilisée dans le msg d'erreur
 *               (défaut: "minimum")
 */
const DateInput = ({
  value,
  onChange,
  testId,
  style,
  disabled = false,
  minDate,
  minLabel = 'minimum',
  // Nouveaux props (2026-02-15) — permet d'utiliser DateInput comme
  // drop-in replacement pour `<input type="date">` dans InfoFinModal
  // (min/max), FIRWizard (onBlur/onKeyDown) et App.js (max).
  min,
  max,
  onBlur: onBlurProp,
  onKeyDown,
}) => {
  const [display, setDisplay] = useState(value || '');
  const [error, setError] = useState('');

  // Compat : `min` (format ISO AAAA-MM-JJ) est traité comme `minDate` s'il
  // n'est pas déjà fourni. Fonctionne en pass-through pour les composants
  // qui utilisent la sémantique `<input type="date"> min="..."`.
  const effectiveMinDate = minDate || min || '';
  const effectiveMaxDate = max || '';

  useEffect(() => {
    setDisplay(value || '');
    setError('');
  }, [value]);

  // Validation centralisée : appelée à la fois onChange (dès 8 chiffres saisis)
  // et onBlur. Retourne {valid, dateStr, errorMsg}. Ne propage rien.
  const validate = (digits) => {
    if (digits.length === 0) return { valid: true, dateStr: '', errorMsg: '' };
    if (digits.length !== 8) return { valid: false, dateStr: '', errorMsg: 'Format requis : AAAAMMJJ' };
    const yyyy = parseInt(digits.slice(0, 4));
    const mm   = parseInt(digits.slice(4, 6));
    const dd   = parseInt(digits.slice(6, 8));
    const dateStr = `${String(yyyy).padStart(4,'0')}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
    const d = new Date(yyyy, mm - 1, dd);
    const reelle = !isNaN(d.getTime()) && d.getFullYear() === yyyy && (d.getMonth() + 1) === mm && d.getDate() === dd;
    if (!reelle) return { valid: false, dateStr: '', errorMsg: 'Date invalide' };
    if (minDate && dateStr < minDate) {
      return { valid: false, dateStr, errorMsg: `La date ne peut pas être antérieure à la ${minLabel} (${minDate})` };
    }
    return { valid: true, dateStr, errorMsg: '' };
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/[^\d]/g, '');
    if (digits.length > 8) return;

    let formatted = digits;
    if (digits.length > 4) formatted = digits.slice(0, 4) + '-' + digits.slice(4);
    if (digits.length > 6) formatted = digits.slice(0, 4) + '-' + digits.slice(4, 6) + '-' + digits.slice(6);

    setDisplay(formatted);

    // Validation IMMÉDIATE dès que 8 chiffres sont saisis — l'utilisateur voit
    // l'erreur en temps réel, avant même de quitter le champ (parité UX modernes).
    // On propage aussi la valeur au parent si valide, pour que la sauvegarde
    // sans focus-out ne perde pas la donnée.
    if (digits.length === 8) {
      const { valid, dateStr, errorMsg } = validate(digits);
      setError(errorMsg);
      if (valid) onChange(dateStr);
    } else {
      setError('');
    }
  };

  const handleBlur = (e) => {
    const digits = display.replace(/-/g, '');
    const { valid, dateStr, errorMsg } = validate(digits);
    setError(errorMsg);
    if (valid) {
      if (digits.length === 8) setDisplay(dateStr);
      onChange(dateStr);
    }
    // Propage l'événement blur au parent si demandé (utilisé par FIRWizard
    // pour avancer au champ suivant après saisie complète).
    if (typeof onBlurProp === 'function') onBlurProp(e);
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 1}}>
      <input
        className="win-input"
        type="text"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        placeholder="AAAA-MM-JJ"
        maxLength={10}
        disabled={disabled}
        data-testid={testId}
        style={{borderColor: error ? '#c00' : undefined, ...style}}
      />
      {error && (
        <span style={{fontSize: '10px', color: '#c00', lineHeight: 1.2}} data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </span>
      )}
    </div>
  );
};

export default DateInput;
